/** @file template.instantiate: the puppet becomes a built-in template (empty slots, scoped), clips on ask. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { puppetInverse } from '../apply/puppetInverse';
import { checkClip } from '../puppetDoc/checkClip';
import { orderNodes } from '../node/orderNodes';
import { requireDrawnAzimuths } from '../facet/requireDrawnAzimuths';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { Command } from '../types/command';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { requireProfile } from '../validate/requireProfile';
import { applyScope } from './applyScope';
import { readScope } from './readScope';
import { resetSlots } from './resetSlots';

/**
 * `template.instantiate { template, name?, profile?, scope?, clips? }` → `{ puppet, slots, clips }`: the template puppet
 * with a new id, entries as authored (narrowed by `applyScope` when `scope` is given, `SCOPE_PARTIAL` naming what it
 * could not narrow) and every slot empty; an entry whose table draws a tile only mirrored (a `_FLIP` twin) without
 * `drawnAzimuths` is VALIDATION. `clips: 'none'` (CR-015, the default) leaves the document's clips as they are and
 * replies `clips: []`; `'all'` gives each template clip a new id, replacing a same-named clip (other clips stay).
 * Inverse: re-import the previous puppet, then (with `'all'`) delete or re-import each template clip name.
 */
export const templateInstantiateCommand: CommandDef<'template.instantiate'> = {
  name: 'template.instantiate',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['template', 'name', 'profile', 'scope', 'clips']);
    return compact({
      template: r.str('template'), name: r.opt('name', r.str), profile: r.opt('profile', r.str),
      scope: r.opt('scope', (k) => readScope(r.raw(k), r.at(k))), clips: r.opt('clips', (k) => r.oneOf(k, ['none', 'all'] as const)),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const t = ctx.templates[input.template]
      ?? throwError(ERROR_CODES.NOT_FOUND, { template: input.template }, { path: '/input/template', hint: `templates: ${Object.keys(ctx.templates).join(', ')}` });
    const profile = requireProfile(input.profile ?? t.puppet.profile, ctx);
    const clips = input.clips === 'all' ? t.clips : [];
    const inverse: Command[] = [...puppetInverse(draft)];
    for (const c of clips) {
      const prev = draft.clips[c.name];
      inverse.push(prev === undefined ? { name: 'clip.delete', input: { clip: c.name } } : clipInverse(prev));
    }
    const fresh = { ...resetSlots(structuredClone(t.puppet)), id: ctx.makeId('pup'), name: input.name ?? t.puppet.name, profile };
    const scoped = input.scope === undefined ? { puppet: fresh, warnings: [] } : applyScope(fresh, input.scope, ctx);
    scoped.puppet.drawables.forEach((d, di) => {
      const facets = d.kind === 'impostor' || d.kind === 'effect' ? (d.facets ?? []) : [];
      facets.forEach((e, i) => { requireDrawnAzimuths(scoped.puppet, e, `/puppet/drawables/${di}/facets/${i}`); });
    });
    draft.puppet = scoped.puppet;
    draft.puppet.nodes = orderNodes(draft.puppet.nodes);
    for (const c of clips) {
      draft.clips[c.name] = { ...structuredClone(c), id: ctx.makeId('clp') };
      checkClip(c.name, draft.clips[c.name] ?? c);
    }
    checkPuppet(draft, ctx);
    return { result: { puppet: draft.puppet.id, slots: deriveSlots(draft.puppet), clips: clips.map((c) => c.name) }, warnings: scoped.warnings, inverse };
  },
};
